import { useRef } from 'react';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import { useSnackbar } from 'notistack';

import useUiAlerts from '../../stores/AlertsStore';
import { ERROR, WARNING } from '../../stores/ToastStore';
import { useIsAdminUser } from '../../stores/SessionStore';
import { useGlobalResponseInterceptor } from '../../services/ApiDataService';
import { LICENSE_COLLECTOR_HEADER_TEXT, LICENSE_SERVER_HEADER_TEXT } from '../pages-admin/license/LicenseConstants';

const INVALID_LICENSE = 'invalid';
const VALID_LICENSE = 'valid';
const LICENSE_TOAST_TIMEOUT_MS = 30 * 1000;
const SERVER_LICENSE_HEADER = 'ngp-server-license';
const LICENSE_AGENT_2K_HEADER_TEXT = t`nPoint 2000`;
const LICENSE_AGENT_3K_HEADER_TEXT = t`nPoint 3000`;
const LICENSE_HEADERS = [
    [SERVER_LICENSE_HEADER, LICENSE_SERVER_HEADER_TEXT],
    ['ngp-collector-license', LICENSE_COLLECTOR_HEADER_TEXT],
    ['ngp-agent2k-license', LICENSE_AGENT_2K_HEADER_TEXT],
    ['ngp-agent3k-license', LICENSE_AGENT_3K_HEADER_TEXT],
];

function buildLicenseAlerts(response) {
    return LICENSE_HEADERS.reduce((licenseAlerts, [headerName, trialType]) => {
        if (response?.headers?.get(headerName)) {
            const headerValue = response.headers.get(headerName);
            const translatedTrialType = i18n._(trialType);

            if (headerValue === VALID_LICENSE) {
                if (headerName === SERVER_LICENSE_HEADER) {
                    licenseAlerts.push({ notificationType: 'clear-alert' });
                }

                return licenseAlerts;
            }

            if (headerValue === INVALID_LICENSE) {
                licenseAlerts.push({
                    text: i18n._(t`The current ${translatedTrialType} License evaluation period has expired.`),
                    notificationType: headerName === SERVER_LICENSE_HEADER ? 'alert' : 'toast',
                    type: ERROR,
                });

                return licenseAlerts;
            }

            licenseAlerts.push({
                text: i18n._(
                    t`The current ${translatedTrialType} License evaluation period is expiring in ${headerValue} day(s). Go to the Administration > Licensing page for details.`
                ),
                notificationType: 'toast',
                type: WARNING,
            });
        }

        return licenseAlerts;
    }, []);
}

export default function ServerStatusProvider() {
    const [uiAlertState, uiAlertActions] = useUiAlerts();
    const [isAdminUser] = useIsAdminUser();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const lastLicenseAlertText = useRef(null);
    const shownLicenseToastTexts = useRef(new Set());

    useGlobalResponseInterceptor('server-status', (responseBody, response) => {
        if (!isAdminUser) {
            return;
        }

        const nextLicenseAlerts = buildLicenseAlerts(response);

        nextLicenseAlerts.forEach((nextLicenseAlert) => {
            if (nextLicenseAlert.notificationType === 'clear-alert') {
                if (lastLicenseAlertText.current && uiAlertState?.text === lastLicenseAlertText.current) {
                    uiAlertActions.handleRemove();
                    lastLicenseAlertText.current = null;
                }

                return;
            }

            if (nextLicenseAlert.notificationType === 'alert') {
                lastLicenseAlertText.current = nextLicenseAlert.text;

                if (uiAlertState?.text !== nextLicenseAlert.text) {
                    uiAlertActions.createErrorAlert(nextLicenseAlert.text);
                }
            }

            if (
                nextLicenseAlert.notificationType === 'toast' &&
                !shownLicenseToastTexts.current.has(nextLicenseAlert.text)
            ) {
                shownLicenseToastTexts.current.add(nextLicenseAlert.text);
                const snackbarKey = enqueueSnackbar(nextLicenseAlert.text, {
                    autoHideDuration: LICENSE_TOAST_TIMEOUT_MS,
                    onClickCapture: () => closeSnackbar(snackbarKey),
                    onClick: () => closeSnackbar(snackbarKey),
                });
            }
        });
    });

    return null;
}
